import { ApolloClient, DataProxy, useApolloClient } from '@apollo/client';
import { Info } from '@mui/icons-material';
import DatePicker from '@mui/lab/DatePicker';
import {
  Box,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  useTheme,
} from '@mui/material';
import {
  MaterialDraft,
  NotificationTypeEnum,
  useAdvice,
  useNotification,
} from '@prismamedia/one-components';
import { InputGroup, useEditContext } from '@prismamedia/one-crud-react';
import { useCompareNlWithNlf } from 'NLFactory/hooks/useCompareNlWithNlf';
import { ContainerSuggestionAi } from 'components/ContainerSuggestionAi';
import {
  ContentState,
  EditorState,
  convertFromRaw,
  convertToRaw,
} from 'draft-js';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPromptSubjectAndPreHeader } from 'utils/openAi';
import {
  BlockFragment,
  GetCampaignCountQuery,
  GetCampaignCountQueryVariables,
  GetNewsletterQuery_newsletter_Newsletter,
  Section,
} from '../../../../../__generated__/queries-newsletter';
import { GET_CAMPAIGN_COUNT } from '../../../../../apollo/queries/getCampaigns.newsletter.graphql';
import { useGetNewsletters } from '../../../../../apollo/queries/getNewsletters.newsletter.graphql';
import { BasicContainer } from '../../../../../components/BasicContainer';
import { useQueryParams } from '../../../../../utils/useUriHelpers';
import { checkDate } from '../../utils';
import { ObjectPopper, PreHeaderPopper } from './Poppers';

interface QueryParams {
  brand: string | null;
  name: string | null;
  availableAt: string | null;
}

type NewBlockType = Omit<BlockFragment, 'title'> & {
  title: EditorState;
};

export const CampaignFields: FC = () => {
  const context = useEditContext();
  const theme = useTheme();

  const { pushNotification } = useNotification();
  const client = useApolloClient() as ApolloClient<DataProxy>;
  const { queryParams } = useQueryParams<QueryParams>({
    brand: null,
    name: null,
    availableAt: null,
  });
  const brandKey = queryParams.brand || context.item.newsletter.brandKey;
  const [fieldsState, setFieldsState] = useState<Record<string, any>>({
    availableAt: context.item.availableAt,
    newsletter: context.item.newsletter,
    preHeader: JSON.stringify(
      convertToRaw(ContentState.createFromText(context.item.preHeader || '')),
    ),
    subject: JSON.stringify(
      convertToRaw(ContentState.createFromText(context.item.subject || '')),
    ),
    utmContent: context.item.utmContent || '',
  });

  const { data: nlData } = useGetNewsletters({
    first: 100,
    where: { brandKey },
  });
  useCompareNlWithNlf(nlData?.newsletters, brandKey);
  const { openAdvice } = useAdvice();
  const objectInfoRef = useRef(null);
  const subjectFieldRef = useRef(null);
  const preHeaderInfoRef = useRef(null);
  const preHeaderFieldRef = useRef(null);

  const setNewsletter = useCallback(
    (newsletter: GetNewsletterQuery_newsletter_Newsletter) => {
      setFieldsState((prevState) => ({
        ...prevState,
        newsletter,
        preHeader: JSON.stringify(
          convertToRaw(
            ContentState.createFromText(
              newsletter.campaignPreHeaderPattern || prevState.preHeader,
            ),
          ),
        ),
        subject: JSON.stringify(
          convertToRaw(
            ContentState.createFromText(
              newsletter.campaignSubjectPattern || prevState.subject,
            ),
          ),
        ),
        utmContent: '',
      }));
    },
    [],
  );

  const handleChange = useCallback(
    (field: string) => (value: string) => {
      // bypass react's event polling
      setFieldsState((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    },
    [],
  );

  const handleDateChange = useCallback(
    async (date: Date | null, newsletterId?: string) => {
      const dateAsMoment = moment(date);
      const isValidDate = dateAsMoment.isValid();

      if (!isValidDate) {
        return;
      }

      const availableAt = dateAsMoment.format('YYYY-MM-DD');
      const { data } = await client.query<
        GetCampaignCountQuery,
        GetCampaignCountQueryVariables
      >({
        query: GET_CAMPAIGN_COUNT,
        variables: {
          where: {
            availableAt,
            newsletter: {
              id: newsletterId || fieldsState.newsletter?.id,
            },
          },
        },
      });

      if (data && data.campaignCount) {
        pushNotification({
          type: NotificationTypeEnum.error,
          message:
            'Une campagne existe déjà à cette date pour cette newsletter.',
        });
      }

      setFieldsState((prevState) => ({
        ...prevState,
        availableAt: data?.campaignCount ? null : availableAt,
      }));
    },
    [client, fieldsState.newsletter, pushNotification],
  );

  const handleNewsletterChange = useCallback(
    (e: SelectChangeEvent<any>) => {
      const newsletter = nlData?.newsletters.find(
        ({ id }) => id === e.target.value,
      );

      newsletter && setNewsletter(newsletter);
    },
    [nlData?.newsletters, setNewsletter],
  );

  const handleInfoIconClick = useCallback(
    (anchorRef, content) => () => {
      openAdvice({
        anchorRef,
        content,
        PopperProps: {
          keepMounted: false,
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundariesElement: 'window',
                enabled: false,
                escapeWithReference: true,
              },
            },
          ],
          style: { width: '310px' },
        },
        zIndex: 1,
      });
    },
    [openAdvice],
  );

  const fieldsData = useMemo(
    () => ({
      preHeader: {
        onConfirm: handleChange('preHeader'),
        value: context.item.preHeader,
      },
      subject: {
        onConfirm: handleChange('subject'),
        value: context.item.subject,
      },
    }),
    [context.item.preHeader, context.item.subject, handleChange],
  );

  // Retrieve titles and clean html tags
  const titlesContent = useMemo(() => {
    if (context.item.sections) {
      const blocks = context.item.sections
        .map((section: Section) => section.blocks)
        .flat();

      return blocks
        ? blocks
            .reduce(
              (
                acc: BlockFragment[],
                currentValue: BlockFragment | NewBlockType,
              ) => {
                // Title is EditorState when creating a new Campaign
                if (
                  currentValue.title &&
                  typeof currentValue.title === 'object'
                ) {
                  const plainTextTitle = currentValue.title
                    .getCurrentContent()
                    .getPlainText();
                  return acc ? `${acc}, ${plainTextTitle}` : plainTextTitle;
                }
                return acc
                  ? `${acc}, ${currentValue.title}`
                  : currentValue.title;
              },
              '',
            )
            .replace(/(<([^>]+)>)/gi, '')
            .replaceAll('&nbsp;', '')
        : '';
    }
    return '';
  }, [context]);

  const prompt = useMemo(
    () =>
      getPromptSubjectAndPreHeader(
        titlesContent,
        fieldsData['subject'].value,
        fieldsData['preHeader'].value,
      ),
    [fieldsData, titlesContent],
  );

  useEffect(() => {
    if (queryParams.name && nlData) {
      const newsletter = nlData.newsletters.find(
        ({ name }) => name === queryParams.name,
      );
      newsletter && setNewsletter(newsletter);
      if (queryParams.availableAt) {
        handleDateChange(new Date(queryParams.availableAt), newsletter?.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nlData]);

  useEffect(() => {
    // eslint-disable-next-line immutable/no-mutation
    context.item = {
      ...context.item,
      ...fieldsState,
      preHeader:
        fieldsState.preHeader !== ''
          ? convertFromRaw(JSON.parse(fieldsState.preHeader)).getPlainText()
          : '',
      subject:
        fieldsState.subject !== ''
          ? convertFromRaw(JSON.parse(fieldsState.subject)).getPlainText()
          : '',
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsState, context.item]);

  return (
    <>
      <Grid sx={{ padding: `${theme.spacing(2)} 0`, width: '100%' }}>
        <BasicContainer title="Template">
          <Box sx={{ mb: 2 }}>
            <InputGroup>
              <div>
                <InputLabel sx={{ transform: 'scale(0.75)' }}>
                  Template
                </InputLabel>
                <Select
                  value={fieldsState.newsletter.id || ''}
                  onChange={handleNewsletterChange}
                  displayEmpty
                  fullWidth
                  variant="standard"
                >
                  <MenuItem value="" />
                  {(nlData?.newsletters || []).map(({ id, name }) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </InputGroup>
          </Box>
        </BasicContainer>
      </Grid>

      <Grid sx={{ padding: `${theme.spacing(2)} 0`, width: '100%' }}>
        <BasicContainer title="Date d'envoi">
          <Box sx={{ mb: 2 }}>
            <InputGroup>
              <InputLabel sx={{ transform: 'scale(0.75)' }}>
                Date d'envoi
              </InputLabel>
              <DatePicker
                value={fieldsState.availableAt || null}
                disabled={!fieldsState.newsletter.name}
                inputFormat="dd/MM/yyyy"
                onChange={handleDateChange}
                renderInput={(props) => (
                  <TextField {...props} variant="standard" />
                )}
                shouldDisableDate={checkDate}
              />
            </InputGroup>
          </Box>
        </BasicContainer>
      </Grid>
      <Grid sx={{ padding: `${theme.spacing(2)} 0`, width: '100%' }}>
        <ContainerSuggestionAi
          title="Objet et Pré-header"
          disabled={!titlesContent}
          prompt={prompt}
          fieldsData={fieldsData}
        >
          <Box sx={{ mb: 2 }}>
            <InputGroup>
              <MaterialDraft
                wordCounter
                value={fieldsState.subject}
                onChange={handleChange('subject')}
                inputProps={{
                  inputRef: subjectFieldRef,
                }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <div>Objet</div>
                    <Info
                      sx={{ ml: 0.5 }}
                      onClick={handleInfoIconClick(objectInfoRef, ObjectPopper)}
                      ref={objectInfoRef}
                    />
                  </Box>
                }
                actions={[]}
              />
              <MaterialDraft
                wordCounter
                value={fieldsState.preHeader}
                onChange={handleChange('preHeader')}
                inputProps={{
                  inputRef: preHeaderFieldRef,
                }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <div>Pré-header</div>
                    <Info
                      sx={{ ml: 0.5 }}
                      onClick={handleInfoIconClick(
                        preHeaderInfoRef,
                        PreHeaderPopper,
                      )}
                      ref={preHeaderInfoRef}
                    />
                  </Box>
                }
                actions={[]}
              />
            </InputGroup>
          </Box>
        </ContainerSuggestionAi>
      </Grid>
      <Grid sx={{ paddingTop: theme.spacing(2), width: '100%' }}>
        <BasicContainer title="UTM Content">
          <InputGroup>
            <TextField
              label="UTM Content"
              margin="normal"
              fullWidth
              onChange={(e) => handleChange('utmContent')(e.target.value)}
              value={fieldsState.utmContent}
              InputLabelProps={{
                shrink: Boolean(fieldsState.utmContent),
              }}
              variant="standard"
            />
          </InputGroup>
        </BasicContainer>
      </Grid>
    </>
  );
};
